import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/authSlice";
import { api } from "../../lib/api";
import AuthImage from "../../components/AuthImage";

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(typeof v === "string" ? v.replace(" ", "T") : v);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const STATUS_LABELS = {
  pending_review: "Pending Review",
  approved: "Approved",
  rejected: "Rejected",
  changes_requested: "Changes Requested",
  uploaded_to_server: "Uploaded to Server",
};

const STATUS_COLORS = {
  pending_review: "bg-yellow-100 text-yellow-800",
  approved: "bg-blue-100 text-blue-800",
  rejected: "bg-red-100 text-red-800",
  changes_requested: "bg-orange-100 text-orange-800",
  uploaded_to_server: "bg-green-100 text-green-800",
};

const TIME_LABELS = {
  day: "Day (06:00 – 18:00)",
  night: "Night (18:00 – 06:00)",
};

// Returns the src for AuthImage — S3 URL directly, or API path for local pending images
function imagePreviewSrc(img) {
  return `/admin/city-images/${img.id}/preview`;
}

const uploaderName = (batch) => {
  if (batch.uploaded_by_name) return batch.uploaded_by_name;
  if (!batch.uploader) return "—";
  return `${batch.uploader.first_name || ""} ${batch.uploader.last_name || ""}`.trim() || batch.uploader.email || "—";
};

export default function CityImageList() {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const isSuperadmin = Number(user?.role_id) === 1;

  const [batches, setBatches] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", city_code: "", city_name: "", time_of_day: "" });
  const [page, setPage] = useState(1);

  // Review modal state — operates on the representative image of a batch
  const [reviewModal, setReviewModal] = useState(null); // { batch, repImage }
  const [reviewAction, setReviewAction] = useState("");
  const [reviewRemark, setReviewRemark] = useState("");
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState("");

  // Lightbox — shows a specific image
  const [lightbox, setLightbox] = useState(null); // { img, batch }
  const [deletingBatchId, setDeletingBatchId] = useState("");
  const [actionError, setActionError] = useState("");

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (filters.status) params.set("status", filters.status);
      if (filters.city_code) params.set("city_code", filters.city_code);
      if (filters.city_name) params.set("city_name", filters.city_name);
      if (filters.time_of_day) params.set("time_of_day", filters.time_of_day);
      const { data } = await api.get(`/admin/city-images?${params}`);
      setBatches(data.data || []);
      setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
    } catch {
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const openReview = (batch) => {
    setReviewModal(batch);
    setReviewAction("");
    setReviewRemark("");
    setReviewError("");
  };

  const submitReview = async () => {
    if (!reviewAction) {
      setReviewError("Please select an action.");
      return;
    }
    if (reviewAction === "request_changes" && !reviewRemark.trim()) {
      setReviewError("Remark is required when requesting changes.");
      return;
    }
    setReviewLoading(true);
    setReviewError("");
    try {
      // Use the first image's ID as the representative for the batch
      const repId = reviewModal.images[0].id;
      await api.patch(`/admin/city-images/${repId}/review`, {
        action: reviewAction,
        remark: reviewRemark.trim() || undefined,
      });
      setReviewModal(null);
      fetchImages();
    } catch (err) {
      setReviewError(err?.response?.data?.message || "Failed to submit review.");
    } finally {
      setReviewLoading(false);
    }
  };

  const deleteBatch = async (batch) => {
    const ok = window.confirm(
      `Delete this ${batch.city_name} ${batch.time_of_day === "day" ? "day" : "night"} batch and all ${batch.image_count} image${batch.image_count !== 1 ? "s" : ""}?`
    );
    if (!ok) return;

    setDeletingBatchId(batch.batch_id);
    setActionError("");
    try {
      await api.delete(`/admin/city-images/batch/${batch.batch_id}`);
      if (lightbox?.batch?.batch_id === batch.batch_id) setLightbox(null);
      if (reviewModal?.batch_id === batch.batch_id) setReviewModal(null);
      await fetchImages();
    } catch (err) {
      setActionError(err?.response?.data?.message || "Failed to delete batch.");
    } finally {
      setDeletingBatchId("");
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">City Images</h1>
        {!isSuperadmin && (
          <Link
            to="/admin/city-images/upload"
            className="bg-[#5dbfef] text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            + Upload Image
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <select
          value={filters.status}
          onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>

        <select
          value={filters.time_of_day}
          onChange={(e) => { setFilters((f) => ({ ...f, time_of_day: e.target.value })); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
        >
          <option value="">Day & Night</option>
          <option value="day">Day (06:00 – 18:00)</option>
          <option value="night">Night (18:00 – 06:00)</option>
        </select>

        <input
          type="text"
          placeholder="Search by city name"
          value={filters.city_name}
          onChange={(e) => { setFilters((f) => ({ ...f, city_name: e.target.value })); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-48"
        />
        {/* <input
          type="text"
          placeholder="City code (e.g. BOM)"
          value={filters.city_code}
          onChange={(e) => { setFilters((f) => ({ ...f, city_code: e.target.value })); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
        /> */}
      </div>

      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-5">
          {actionError}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p className="text-gray-500 text-sm">Loading…</p>
      ) : batches.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          {/* <p className="text-4xl mb-3">🖼️</p> */}
          <p>No city images found.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Previews</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">City</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">IATA Codes</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Time</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Images</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                  {isSuperadmin && <th className="px-4 py-3 text-left font-medium text-gray-600">Uploaded By</th>}
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {batches.map((batch) => (
                  <tr key={batch.batch_id} className="hover:bg-gray-50 align-top">
                    {/* Thumbnail strip — up to 3 thumbnails then +N badge */}
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {batch.images.slice(0, 3).map((img) => (
                          <button
                            key={img.id}
                            type="button"
                            onClick={() => setLightbox({ img, batch })}
                            className="w-14 h-10 rounded overflow-hidden border border-gray-200 hover:opacity-80 transition flex-shrink-0"
                          >
                            <AuthImage
                              src={imagePreviewSrc(img)}
                              alt={img.original_filename}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                        {batch.images.length > 3 && (
                          <button
                            type="button"
                            onClick={() => setLightbox({ img: batch.images[3], batch })}
                            className="w-14 h-10 rounded border border-gray-200 bg-gray-100 text-gray-500 text-xs font-medium flex items-center justify-center hover:bg-gray-200 transition flex-shrink-0"
                          >
                            +{batch.images.length - 3}
                          </button>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{batch.city_name}</div>
                      <div className="text-xs text-gray-400">{batch.city_code} · {batch.country_name}</div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(batch.iata_codes || []).map((code) => (
                          <span key={code} className="inline-block bg-gray-100 text-gray-700 text-xs px-1.5 py-0.5 rounded">
                            {code}
                          </span>
                        ))}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${batch.time_of_day === "day" ? "bg-amber-50 text-amber-700" : "bg-indigo-50 text-indigo-700"}`}>
                        {batch.time_of_day === "day" ? "☀️ Day" : "🌙 Night"}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-xs text-gray-600">
                      <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full font-medium">
                        {batch.image_count} file{batch.image_count !== 1 ? "s" : ""}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_COLORS[batch.status]}`}>
                        {STATUS_LABELS[batch.status]}
                      </span>
                      {batch.review_remark && (
                        <p className="text-xs text-gray-500 mt-1 max-w-[160px] truncate" title={batch.review_remark}>
                          {batch.review_remark}
                        </p>
                      )}
                    </td>

                    {isSuperadmin && (
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {uploaderName(batch)}
                      </td>
                    )}

                    <td className="px-4 py-3 text-xs text-gray-500">
                      {fmtDate(batch.created_at)}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isSuperadmin && ["pending_review", "changes_requested"].includes(batch.status) && (
                          <button
                            type="button"
                            onClick={() => openReview(batch)}
                            title="Review"
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition border border-blue-200"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                        )}
                        {!isSuperadmin && (batch.can_edit ?? true) && (
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/city-images/edit/${batch.batch_id}`, { state: { batch } })}
                            title="Edit"
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 transition border border-gray-200"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                        )}
                        {!isSuperadmin && (batch.can_delete ?? true) && (
                          <button
                            type="button"
                            onClick={() => deleteBatch(batch)}
                            disabled={deletingBatchId === batch.batch_id}
                            title="Delete"
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition border border-red-200 disabled:opacity-50"
                          >
                            {deletingBatchId === batch.batch_id ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                              </svg>
                            )}
                          </button>
                        )}
                        {((isSuperadmin && !["pending_review", "changes_requested"].includes(batch.status)) ||
                          (!isSuperadmin && batch.can_edit === false && batch.can_delete === false)) && (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4 text-sm text-gray-600">
            <p>{pagination.total} total batch{pagination.total !== 1 ? "es" : ""}</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <span className="px-3 py-1 font-medium">
                Page {page} of {pagination.pages || 1}
              </span>
              <button
                type="button"
                disabled={page >= (pagination.pages || 1)}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Review Modal — reviews all images in the batch at once */}
      {reviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-[95vw] sm:w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">Review </h2>
            <p className="text-sm text-gray-500 mb-4">
              {reviewModal.city_name} · {TIME_LABELS[reviewModal.time_of_day]} · {reviewModal.image_count} image{reviewModal.image_count !== 1 ? "s" : ""}
            </p>

            {/* Thumbnail grid of all images in batch */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {reviewModal.images.map((img) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => setLightbox({ img, batch: reviewModal })}
                  className="relative group text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
                  title="View full screen"
                >
                  <AuthImage
                    src={imagePreviewSrc(img)}
                    alt={img.original_filename}
                    className="w-full h-28 object-cover rounded-lg border group-hover:opacity-85 transition"
                  />
                  <p className="text-[10px] text-gray-500 mt-1 truncate" title={img.original_filename}>
                    {img.original_filename}
                  </p>
                </button>
              ))}
            </div>

            <div className="space-y-2 mb-4">
              {[
                { value: "approve", label: "Approve & Publish to S3", color: "border-green-500 text-green-700" },
                { value: "reject", label: "Reject", color: "border-red-500 text-red-700" },
                { value: "request_changes", label: "Request Changes", color: "border-orange-500 text-orange-700" },
              ].map((opt) => (
                <label key={opt.value} className={`flex items-center gap-3 border rounded-lg px-4 py-2.5 cursor-pointer ${reviewAction === opt.value ? `${opt.color} border-2` : "border-gray-200"}`}>
                  <input
                    type="radio"
                    name="review_action"
                    value={opt.value}
                    checked={reviewAction === opt.value}
                    onChange={() => setReviewAction(opt.value)}
                    className="accent-blue-600"
                  />
                  <span className="text-sm font-medium">{opt.label}</span>
                </label>
              ))}
            </div>

            <textarea
              rows={3}
              value={reviewRemark}
              onChange={(e) => setReviewRemark(e.target.value)}
              placeholder={reviewAction === "request_changes" ? "Remark (required)" : "Remark (optional)"}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
            />

            {reviewError && <p className="text-xs text-red-500 mb-3">{reviewError}</p>}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setReviewModal(null)}
                className="flex-1 border border-gray-300 text-gray-700 text-sm py-2 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitReview}
                disabled={reviewLoading}
                className="flex-1 bg-blue-600 text-white text-sm py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60"
              >
                {reviewLoading ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox — browse images in the batch */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="relative w-full h-full flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="absolute top-3 right-3 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full w-10 h-10 flex items-center justify-center text-2xl leading-none"
              aria-label="Close full screen preview"
            >
              ×
            </button>
            <AuthImage
              src={imagePreviewSrc(lightbox.img)}
              alt={lightbox.img.original_filename}
              className="max-w-full max-h-[82vh] object-contain rounded-lg"
            />
            <p className="text-white text-sm text-center mt-3 opacity-70">
              {lightbox.batch.city_name} · {TIME_LABELS[lightbox.batch.time_of_day]} · {lightbox.img.original_filename}
            </p>

            {/* Batch thumbnail strip at bottom */}
            {lightbox.batch.images.length > 1 && (
              <div className="flex justify-center gap-2 mt-3 max-w-full overflow-x-auto px-2">
                {lightbox.batch.images.map((img) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setLightbox({ img, batch: lightbox.batch })}
                    className={`w-12 h-9 rounded overflow-hidden border-2 transition flex-shrink-0 ${lightbox.img.id === img.id ? "border-white" : "border-transparent opacity-60 hover:opacity-100"}`}
                  >
                    <AuthImage
                      src={imagePreviewSrc(img)}
                      alt={img.original_filename}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
